/**
 * Azure Copilot Studio API Client
 * Provides methods for interacting with the ASP.NET Core API that connects to Copilot Studio
 */

import axios from 'axios';
import { API_CONFIG } from '../config/constants';

export class CopilotAPI {
  /**
   * Initialize the Copilot API client
   * @param {string} accessToken - Azure AD access token (JWT)
   * @param {Object} connectWiseUser - ConnectWise user info from getMemberAuthentication
   */
  constructor(accessToken, connectWiseUser = null) {
    this.connectWiseUser = connectWiseUser;
    this.client = axios.create({
      baseURL: API_CONFIG.apiUrl,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      timeout: 60000 // 60 second timeout for bot responses
    });

    console.log('Copilot API client initialized');
  }

  /**
   * Generate a unique conversation ID for a ticket session
   * Format: ticket-{ticketId}-{timestamp}-{random}
   * @private
   * @param {number} ticketId - Ticket ID
   * @returns {string} Unique conversation ID
   */
  generateConversationId(ticketId) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `ticket-${ticketId}-${timestamp}-${random}`;
  }

  /**
   * Analyze a ConnectWise ticket using the Copilot bot
   * The bot will fetch the ticket details from ConnectWise
   * @param {Object} ticketData - Object with ticket ID
   * @returns {Promise<Object>} Bot response with summary and suggestions
   */
  async analyzeTicket(ticketData) {
    try {
      const ticketId = ticketData.id;

      // Generate unique conversation ID for this session
      const conversationId = this.generateConversationId(ticketId);

      console.log('[Pod] Sending ticket analysis request to Copilot bot:', ticketId);
      console.log('[Pod] Conversation ID:', conversationId);

      // Simple message - bot will fetch the ticket details from ConnectWise
      const message = `Please analyze ConnectWise ticket #${ticketId} and provide a brief summary of the issue`;

      // Call the ASP.NET API's bot invocation endpoint
      const response = await this.client.post(API_CONFIG.endpoints.invokeBot, {
        message: message,
        conversationId: conversationId,
        userId: this.connectWiseUser?.userId || 'pod-user',
        userUpn: this.connectWiseUser?.userEmail || 'unknown',
        context: {
          ticketId: ticketId,
          source: 'connectwise-pod',
          sessionId: conversationId
        }
      });

      console.log('[Pod] Copilot analysis received');
      return response.data;
    } catch (error) {
      console.error('[Pod] Error analyzing ticket:', error);
      throw new Error(`Failed to analyze ticket: ${error.message}`);
    }
  }


  /**
   * Send a follow-up message in an existing conversation
   * @param {string} conversationId - Conversation ID
   * @param {string} message - Follow-up message
   * @returns {Promise<Object>} Bot response
   */
  async sendFollowUp(conversationId, message) {
    try {
      const response = await this.client.post(API_CONFIG.endpoints.invokeBot, {
        message,
        conversationId,
        userId: this.connectWiseUser?.userId || 'system',
        userUpn: this.connectWiseUser?.userEmail || 'unknown'
      });

      return response.data;
    } catch (error) {
      console.error('Error sending follow-up:', error);
      throw new Error(`Failed to send follow-up: ${error.message}`);
    }
  }

  /**
   * Get conversation history
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Array>} Array of conversation activities
   */
  async getConversationHistory(conversationId) {
    try {
      const response = await this.client.get(
        `${API_CONFIG.endpoints.conversations}/${conversationId}/activities`
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      throw new Error(`Failed to fetch conversation history: ${error.message}`);
    }
  }

  /**
   * Check API health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      const response = await this.client.get(API_CONFIG.endpoints.health);
      return response.data;
    } catch (error) {
      console.error('API health check failed:', error);
      throw new Error(`API health check failed: ${error.message}`);
    }
  }
}
